/**
 * Phase 7+ — Buffets block.
 *
 * Iterates `meal_plan.buffets[]` (one entry per buffet-type lib restaurant)
 * and renders one OvcBlock-style card per buffet. The list of meals
 * surfaced for each buffet is driven by the lib doc's `meals_offered`
 * array (defaults to `breakfast, lunch, dinner`); brunch and tea_time
 * only show up when this buffet opts in. Embarkation Lunch is automatic
 * on Day 1 regardless of meals_offered.
 *
 * Empty state: if there are no buffet-type restaurants in the library,
 * render a friendly inline note instead of an empty container.
 */
import OvcBlock from "@/components/mealplan/OvcBlock";

// Phase 7+ / Phase 13 — chronological meal order across the whole day.
// All three embarkation slots are Day-1-only and slot in alongside
// their regular counterparts (Embark BF before Breakfast, Embark Lunch
// just after Lunch, Embark Dinner just before Dinner).
const ALL_BUFFET_MEALS = [
  "embarkation_breakfast",
  "breakfast",
  "brunch",
  "lunch",
  "embarkation_lunch",
  "tea_time",
  "embarkation_dinner",
  "dinner",
];

// Phase 13 — list of all Day-1-only embarkation meal keys, used by the
// buffet builder below to decide which extra slots to surface on Day 1
// regardless of `meals_offered`.
const EMBARK_MEALS = [
  "embarkation_breakfast",
  "embarkation_lunch",
  "embarkation_dinner",
];

const emptyMealBlock = () => ({
  theme_buffet_id: null,
  theme_buffet_name: "",
  hot_kitchen: [],
  cold_kitchen: [],
  pastry: [],
  lines: [],
  live_station: {
    enabled: false, live_station_id: null, name: "",
    description: "", dishes: [],
  },
  festivals: [],
});

export default function BuffetsBlock({
  value,
  onChange,
  themeBuffetsLib,
  liveStationsLib,
  standardStationsLib,
  // Phase 7++++ — buffet materials library for the per-dish material picker.
  materialsLib,
  festivalsLib,
  restaurantsLib,
  dayNumber,
  // Phase 14 — ship-buffet-line list (pass-through to OvcBlock).
  linesLib,
}) {
  const buffets = Array.isArray(value) ? value : [];
  const isEmbark = Number(dayNumber) === 1;

  // Build a quick lookup so we can read each buffet's `meals_offered` from
  // the library doc.
  const libById = {};
  for (const r of restaurantsLib || []) libById[r.id] = r;

  const updateBuffet = (idx, patch) => {
    const next = buffets.slice();
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };

  if (buffets.length === 0) {
    return (
      <section
        className="rounded-lg border border-dashed bg-muted/30 p-6 text-center"
        data-testid="buffets-block-empty"
      >
        <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
          Buffets
        </h3>
        <p className="mt-2 text-sm text-muted-foreground">
          No buffet-type restaurants in the library yet. Add one in
          <b> Settings → Library → Restaurants</b> (type = Buffet) — e.g.
          OVC or Windjammer — and it will appear here on every voyage day.
        </p>
      </section>
    );
  }

  return (
    <div className="space-y-4" data-testid="buffets-block">
      {buffets.map((bf, idx) => {
        const lib = libById[bf.restaurant_id];
        // Drive which meals to surface from the buffet's lib doc, falling
        // back to the classic three when the buffet is missing or has no
        // meals_offered configured. Phase 13 — `meals_offered` may now
        // include the 3 embarkation meal keys; they pass through only on
        // Day 1 (`isEmbark`).
        const offered = (lib?.meals_offered || ["breakfast", "lunch", "dinner"])
          .filter((m) => ALL_BUFFET_MEALS.includes(m))
          .filter((m) => isEmbark || !EMBARK_MEALS.includes(m));
        // Build the per-buffet value the way OvcBlock expects it:
        // {meal: <block>, …}. Phase 13 — every embarkation slot
        // (breakfast/lunch/dinner) is auto-surfaced on Day 1 regardless
        // of `meals_offered`, matching the existing embarkation_lunch
        // behavior so the user can still attach Day-1 menus to legacy
        // buffet docs that pre-date the meals_offered edit.
        const buffetValue = {};
        for (const m of offered) {
          buffetValue[m] = bf[m] || emptyMealBlock();
        }
        const meals = offered.slice();
        if (isEmbark) {
          for (const em of EMBARK_MEALS) {
            buffetValue[em] = bf[em] || emptyMealBlock();
            if (!meals.includes(em)) meals.push(em);
          }
          // Re-sort into canonical chronological order so the rendered
          // sub-blocks read left-to-right as the day progresses.
          const orderedMeals = ALL_BUFFET_MEALS.filter((m) => meals.includes(m));
          meals.length = 0;
          for (const m of orderedMeals) meals.push(m);
        }
        const title =
          (bf.restaurant_name || "Buffet") +
          " Buffet — Independent themes per meal";

        return (
          <OvcBlock
            key={bf.restaurant_id || idx}
            value={buffetValue}
            onChange={(nextVal) => {
              const patch = {};
              for (const m of meals) {
                if (EMBARK_MEALS.includes(m) && !isEmbark) continue;
                patch[m] = nextVal[m];
              }
              updateBuffet(idx, patch);
            }}
            themeBuffetsLib={themeBuffetsLib}
            liveStationsLib={liveStationsLib}
            standardStationsLib={standardStationsLib}
            materialsLib={materialsLib}
            festivalsLib={festivalsLib}
            linesLib={linesLib}
            title={title}
            testId={`buffet-card-${bf.restaurant_id || idx}`}
            buffetId={`buffet-${bf.restaurant_id || idx}`}
            meals={meals}
          />
        );
      })}
    </div>
  );
}
